import { useEffect, useState, useRef } from 'react';
import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, CardBody, Col, Table } from 'reactstrap';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import './TravelRequestOptions.scss';
import { fetchTravelRequestOptions } from '../../../store/apis/travelRequestService';
import { CLIENT_COLUMNS, HEADER_LABELS } from './constants';
import FlightOptions from './FlightOptions';
import FlightInformation from './FlightInformation';
import HotelInformation from './HotelInformation';
import HotelOptions from './HotelOptions';
import ExpensiveReason from './ExpensiveReason';
import { selectOption } from '../../../store/apis/travelService';
import { Badge } from '../../../components/Common';
import { renderStatusColors } from '../PendingTravelRequest/constants';

moment.locale('en-gb');
function TravelRequestOptions() {
  document.title = 'Travel Request Quote Option | Envision Daikin';
  const navigate = useNavigate();
  const { requestNo, revisionNo } = useParams();
  const flightOptionsRef = useRef(null);
  const hotelOptionsRef = useRef(null);
  const quoteSelectReasonRef = useRef(null);
  const [searchParams] = useSearchParams();
  const requestViewMode = searchParams.get('m') || null;

  const [isParamsReady, setIsParamsReady] = useState(false);

  const isView = requestViewMode === 'View';

  useEffect(() => {
    if (requestNo && revisionNo) {
      // Ensure that requestNo and revisionNo are valid before setting isParamsReady
      setIsParamsReady(true);
    }
  }, [requestNo, revisionNo]);

  const { data: requestOptions, isLoading } = useQuery({
    queryKey: ['requestOptions', requestNo, revisionNo],
    queryFn: () => fetchTravelRequestOptions({ requestNo, revisionNo }),
    enabled: isParamsReady,
    onError: () => {
      toast.error('Failed to fetch travel request options', {
        className: 'toast-error',
      });
    },
  });

  const { mutate: selectTravelOption } = useMutation({
    mutationFn: async payload => selectOption(payload),
    onSuccess: response => {
      if (response?.flag === 'True') {
        toast.success('Option selected successfully!', {
          className: 'toast-success',
        });
        navigate('/travel-request');
      } else if (response?.flag === 'False') {
        toast.error(response?.message || 'Failed to select option!', {
          className: 'toast-error',
        });
      }
    },
  });

  if (isLoading) {
    return (
      <div className="page-content d-flex justify-content-center align-items-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  const { headerDetails, flightDetails, hotelDetails, clientDetails } =
    requestOptions || {};

  const handleSelectOption = requestMode => {
    const selectedFlight = flightOptionsRef?.current;
    const selectedHotel = hotelOptionsRef?.current;
    const quoteSelectReason =
      quoteSelectReasonRef?.current?.quoteSelectReason || '';

    // Validate the selected flight and hotel options
    if (headerDetails?.Flight === 'Y') {
      if (selectedFlight === undefined) {
        toast.error('Please select a flight option', {
          className: 'toast-error',
        });
        return;
      }
      if (!selectedFlight?.isCheapestFlight && !quoteSelectReason) {
        toast.error(
          'Please provide a reason for not selecting the cheapest option',
          {
            className: 'toast-error',
          },
        );
        return;
      }
    }
    if (headerDetails?.Hotel === 'Y') {
      if (selectedHotel === undefined) {
        toast.error('Please select a hotel option', {
          className: 'toast-error',
        });
        return;
      }
      if (!selectedHotel?.isCheapestHotel && !quoteSelectReason) {
        toast.error(
          'Please provide a reason for not selecting the cheapest option',
          {
            className: 'toast-error',
          },
        );
        return;
      }
    }

    const payload = {
      reqNo: requestNo,
      revisionNo: parseInt(revisionNo, 10),
      flightOptionNo: selectedFlight?.selectedFlight ?? 0,
      hotelOptionNo: selectedHotel?.selectedHotel ?? 0,
      quoteSelectReason,
      userAction: requestMode,
    };

    selectTravelOption(payload);
  };

  const isPending = headerDetails?.Status === 'Pending for Decision';
  const isArrayNotEmpty = arr => Array.isArray(arr) && arr.length > 0;

  return (
    <div className="page-content">
      <div className="trd-header d-flex flex-row align-items-center justify-content-between">
        <Col className="d-flex flex-row align-items-center gap-2">
          <button
            type="button"
            className="btn btn-link p-0 back-btn fs-5"
            onClick={() => navigate(-1)}
          >
            <Icon path={mdiChevronLeft} size={1.1} />
            <span className="d-flex flex-row gap-2 fs-5">Back</span>
          </button>
          <div className="trd-separator" />
          <div className="d-flex flex-row align-items-center gap-2 fs-5">
            <span
              className="fw-semibold fs-5"
              style={{ color: '#065A86', fontSize: '14px' }}
            >
              Request ID
            </span>
            <div className="trd-request-header fs-5">{requestNo}</div>
          </div>
          <div className="trd-separator" />
          <div className="d-flex flex-row align-items-center gap-2 fs-5">
            <span
              className="fw-semibold fs-5"
              style={{ color: '#065A86', fontSize: '14px' }}
            >
              Status
            </span>
            <Badge
              config={renderStatusColors?.[headerDetails?.Status]}
              dot
              className="p-2"
            >
              {headerDetails?.Status}
            </Badge>
          </div>
        </Col>
      </div>
      <div className="d-flex flex-column gap-3">
        {/* General Information */}
        <div className="d-flex flex-column gap-2">
          <div className="fw-semibold">General Information</div>
          <Card className="trd-general-info d-flex">
            <CardBody>
              <div className="d-flex flex-row flex-wrap align-items-center justify-content-start gap-4 mb-2">
                {HEADER_LABELS.map(label => (
                  <Col key={label.key} lg={2}>
                    <div className="trd-general-info-label">{label.label}</div>
                    <div className="fw-semibold">
                      {label?.type === 'date'
                        ? moment(headerDetails?.[label.key]).format(
                            'DD-MM-YYYY',
                          )
                        : headerDetails?.[label.key] || 'N/A'}
                    </div>
                  </Col>
                ))}
                <Col lg={2}>
                  <div className="trd-general-info-label">Traveler Name</div>
                  <div className="fw-semibold">
                    {`${headerDetails?.Traveler_FName} ${headerDetails?.Traveler_MName} ${headerDetails?.Traveler_LName}`}
                  </div>
                </Col>
                <Col lg={2}>
                  <div className="trd-general-info-label">Requirements</div>
                  <div className="fw-semibold">
                    {`${headerDetails?.Flight === 'Y' ? 'Flight' : ''} ${headerDetails?.Hotel === 'Y' ? 'Hotel' : ''}`}
                  </div>
                </Col>
                <Col lg={8}>
                  <div className="trd-general-info-label">
                    Purpose of Travel
                  </div>
                  <div className="fw-semibold">
                    {headerDetails?.TravelPurpouse}
                  </div>
                </Col>
              </div>
            </CardBody>
          </Card>
        </div>
        {/* Flight Details */}
        {headerDetails?.Flight?.trim() === 'Y' && (
          <div className="d-flex flex-column gap-2">
            <div className="fw-semibold py-2">Flight Information</div>
            {/* Flight Information */}
            <FlightInformation
              isLoading={isLoading}
              flightDetails={flightDetails}
              showTitle={false}
            />
            <div className="fw-semibold">Flight Booking Options</div>
            {/* Flight Selection */}
            <FlightOptions
              requestOptions={requestOptions}
              showTitle={false}
              isView={isView}
              status={headerDetails.Status}
              onSelectFlightOption={(selectedFlight, isCheapestFlight) => {
                flightOptionsRef.current = { selectedFlight, isCheapestFlight };
              }}
            />
            {/* Hotel Booking Information */}
          </div>
        )}
        {/* Hotel Details */}
        {headerDetails?.Hotel?.trim() === 'Y' && (
          <div className="d-flex flex-column gap-2">
            <div className="fw-semibold py-2">Hotel Information</div>
            <HotelInformation
              showTitle={false}
              isLoading={isLoading}
              hotelDetails={hotelDetails}
            />
            <div className="fw-semibold">Hotel Booking Options</div>
            {/* Hotel Selection */}
            <HotelOptions
              showTitle={false}
              isView={isView}
              status={headerDetails.Status}
              requestOptions={requestOptions}
              onSelectHotelOption={(selectedHotel, isCheapestHotel) => {
                hotelOptionsRef.current = { selectedHotel, isCheapestHotel };
              }}
            />
          </div>
        )}
        {/* Client Details */}
        {isArrayNotEmpty(clientDetails) && (
          <div className="d-flex flex-column gap-2">
            <div className="fw-semibold">Client Information</div>
            <div className="table-responsive">
              <Table
                className="tra-table align-middle text-nowrap table-radius mb-0"
                id="travel-option-table"
              >
                <thead>
                  <tr>
                    {CLIENT_COLUMNS.map((col, index) => (
                      <th key={index}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {clientDetails?.map((client, index) => (
                    <tr key={index}>
                      <td>{client?.ClientName}</td>
                      <td>{client?.Company}</td>
                      <td>{client?.ContactNo}</td>
                      <td>{client?.ProjectName}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        )}
        <ExpensiveReason
          requestOptions={requestOptions}
          onDataChange={reason => {
            quoteSelectReasonRef.current = reason;
          }}
          disabled={!isPending}
        />
      </div>
      {headerDetails?.Status === 'Pending for Decision' && !isView && (
        <div className="d-flex flex-row align-items-center justify-content-center gap-2 py-3">
          <button
            type="button"
            className="custom-btn"
            onClick={() => handleSelectOption('S')}
          >
            Select Option
          </button>
          <button
            type="button"
            className="custom-btn"
            onClick={() => handleSelectOption('A')}
          >
            <span>Select and Submit for Approval</span>
          </button>
        </div>
      )}
    </div>
  );
}

export default TravelRequestOptions;
